import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";

import useTranslation from "src/translations/useTranslation";
import { TravelAnalysisMessages } from "@shared/languages/interfaces";

import { TravelAnalysisAlertRow } from "../../types";

/** Map from raw metadata JSON key to the corresponding i18n enum entry. */
const METADATA_LABEL_MAP: Record<string, string> = {
  // CAN Data Stall
  source: TravelAnalysisMessages.metaSource,
  stall_type: TravelAnalysisMessages.metaStallType,
  threshold: TravelAnalysisMessages.metaThreshold,
  stalled_fields: TravelAnalysisMessages.metaStalledFields,
  total_can_packets: TravelAnalysisMessages.metaTotalCanPackets,
  // Cross Day Cycle
  start_date: TravelAnalysisMessages.metaStartDate,
  finish_date: TravelAnalysisMessages.metaFinishDate,
  start_day: TravelAnalysisMessages.metaStartDay,
  finish_day: TravelAnalysisMessages.metaFinishDay,
  duration_hours: TravelAnalysisMessages.metaDurationHours,
  // False Positive Discharge
  total_discharge_commands: TravelAnalysisMessages.metaTotalDischargeCommands,
  covered_commands: TravelAnalysisMessages.metaCoveredCommands,
  orphan_commands: TravelAnalysisMessages.metaOrphanCommands,
  orphan_ratio: TravelAnalysisMessages.metaOrphanRatio,
  covered_ranges: TravelAnalysisMessages.metaCoveredRanges,
  // GPRS Connection Instability
  total_packets: TravelAnalysisMessages.metaTotalPackets,
  memory_commands: TravelAnalysisMessages.metaMemoryCommands,
  effective_realtime_commands: TravelAnalysisMessages.metaEffectiveRealtimeCommands,
  realtime_commands: TravelAnalysisMessages.metaRealtimeCommands,
  memory_ratio: TravelAnalysisMessages.metaMemoryRatio,
  threshold_percent: TravelAnalysisMessages.metaThresholdPercent,
  effective_realtime_threshold_seconds: TravelAnalysisMessages.metaEffectiveRealtimeThresholdSeconds,
  avg_delay_seconds: TravelAnalysisMessages.metaAvgDelaySeconds,
  max_delay_seconds: TravelAnalysisMessages.metaMaxDelaySeconds,
  // GPS Positioning Failure
  total_gps_packets: TravelAnalysisMessages.metaTotalGpsPackets,
  invalid_fix_count: TravelAnalysisMessages.metaInvalidFixCount,
  invalid_ratio: TravelAnalysisMessages.metaInvalidRatio,
  avg_satellites: TravelAnalysisMessages.metaAvgSatellites,
  // Hardware Command Deficit
  packet_count: TravelAnalysisMessages.metaPacketCount,
  expected_packets: TravelAnalysisMessages.metaExpectedPackets,
  minimum_required: TravelAnalysisMessages.metaMinimumRequired,
  deficit_percent: TravelAnalysisMessages.metaDeficitPercent,
  rate_per_minute: TravelAnalysisMessages.metaRatePerMinute,
  travel_duration_minutes: TravelAnalysisMessages.metaTravelDurationMinutes,
  // Logistics Flow Inconsistency
  required_states: TravelAnalysisMessages.metaRequiredStates,
  actual_states: TravelAnalysisMessages.metaActualStates,
  missing_states: TravelAnalysisMessages.metaMissingStates,
  missing_count: TravelAnalysisMessages.metaMissingCount,
  total_states: TravelAnalysisMessages.metaTotalStates,
  num_doc: TravelAnalysisMessages.metaNumDoc,
  // Sensor Command Deficit (IDLE)
  ignition_on_count: TravelAnalysisMessages.metaIgnitionOnCount,
  idle_count: TravelAnalysisMessages.metaIdleCount,
  loading_count: TravelAnalysisMessages.metaLoadingCount,
  unloading_count: TravelAnalysisMessages.metaUnloadingCount,
  idle_ratio: TravelAnalysisMessages.metaIdleRatio
};

/** Props for the AlertMetadataDialog component. */
interface AlertMetadataDialogProps {
  alert: TravelAnalysisAlertRow | null;
  onClose: () => void;
}

/**
 * Dialog that displays the metadata (analysis details) of a single travel analysis alert.
 * Shows vehicle, alert type, origin/destination, date, and all metadata key-value pairs.
 */
const AlertMetadataDialog: React.FC<AlertMetadataDialogProps> = ({ alert, onClose }) => {

  const { t } = useTranslation();

  if (!alert) return null;

  const metadataEntries = alert.metadata
    ? Object.entries(alert.metadata as Record<string, unknown>)
    : [];

  return (
    <Dialog open={!!alert} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{t(TravelAnalysisMessages.alertDetailsTitle)}</span>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent dividers>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell><strong>{t(TravelAnalysisMessages.columnVehicle)}</strong></TableCell>
              <TableCell>{alert.vehicle.code} — {alert.vehicle.description}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>{t(TravelAnalysisMessages.columnAlertType)}</strong></TableCell>
              <TableCell>{alert.alertType.description}</TableCell>
            </TableRow>
            {alert.numDoc && (
              <TableRow>
                <TableCell><strong>{t(TravelAnalysisMessages.columnNumDoc)}</strong></TableCell>
                <TableCell>{alert.numDoc}</TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell><strong>{t(TravelAnalysisMessages.columnOrigin)}</strong></TableCell>
              <TableCell>{alert.origin.name || `${alert.origin.latitude}, ${alert.origin.longitude}`}</TableCell>
            </TableRow>
            {alert.destination && (
              <TableRow>
                <TableCell><strong>{t(TravelAnalysisMessages.columnDestination)}</strong></TableCell>
                <TableCell>{alert.destination.name || `${alert.destination.latitude}, ${alert.destination.longitude}`}</TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell><strong>{t(TravelAnalysisMessages.columnDate)}</strong></TableCell>
              <TableCell>{new Date(alert.registrationDate).toLocaleString("pt-BR")}</TableCell>
            </TableRow>
            {metadataEntries.length > 0 && metadataEntries.map(([key, value]) => (
              <TableRow key={key}>
                <TableCell>
                  <strong>{METADATA_LABEL_MAP[key] ? t(METADATA_LABEL_MAP[key]) : key}</strong>
                </TableCell>
                <TableCell>
                  {typeof value === "boolean"
                    ? (value ? "Sim" : "Não")
                    : typeof value === "object"
                      ? JSON.stringify(value)
                      : String(value)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t(TravelAnalysisMessages.close)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlertMetadataDialog;
